import { apiClient, FalhaAntesDoFluxo, postEventStream } from '@/shared/lib/api/client';

export type AssistenteLink = {
  path: string;
  rotulo: string;
};

/** Foto ou PDF que a pessoa mandou para a Mia (R22). urlThumb so existe logo depois do envio. */
export type AnexoMia = {
  idArquivo: number;
  nome: string;
  tipo: 'imagem' | 'pdf';
  urlThumb?: string | null;
};

export type SugestaoMomentoMia = { id: string; texto: string; pergunta: string };

/** Saudacao e resumo do dia da conversa vazia (R26); blocos em contracts/schemas/assistente-bloco. */
export type NovidadesMia = { texto: string; blocos: unknown[] };

/** Campo que o cartao deixa editar (R21). selecao: ids separados por virgula. */
export type CampoPropostaMia = {
  chave: string;
  rotulo: string;
  tipo: 'texto' | 'textoLongo' | 'hora' | 'data' | 'opcao' | 'selecao';
  valor: string;
  opcoes: { valor: string; rotulo: string }[];
  max: number;
};

export type AssistenteProposta = {
  id: number;
  ferramenta: string;
  resumo: string;
  expiraEm: string;
  titulo: string;
  texto: string;
  /** Para quem e o registro: aluno, turma ou publicacao. */
  nomeAluno: string;
  campos?: CampoPropostaMia[];
  /** Nomes dos arquivos que vao junto (recado com foto). */
  anexos?: string[];
};

/** Formato em contracts/schemas/assistente-bloco.schema.json. */
export type AcaoMia = {
  tipo: 'navegar' | 'perguntar';
  rotulo: string;
  path?: string;
  texto?: string;
};

export type TipoRegistroMia = 'recado' | 'acesso' | 'saude' | 'soninho' | 'alimentacao' | 'higiene';

/** O que o painel faz sozinho (navegar, destacar, abrir registro) ou com Confirmar (trocar escola). */
export type AcaoInterfaceMia =
  | { tipo: 'navegar'; rotulo: string; path: string }
  | { tipo: 'destacar'; rotulo: string; alvo: string }
  | {
      tipo: 'abrir_registro';
      rotulo: string;
      path: string;
      registro: { tipo: TipoRegistroMia; idAluno: number; nomeAluno?: string; titulo?: string; texto?: string };
    }
  | { tipo: 'trocar_escola'; rotulo: string; idEscola: number };

export type LinhaMia = { rotulo: string; valor: string; destaque?: boolean };

export type BlocoMia =
  | {
      tipo: 'cartao_pessoa';
      pessoa: 'ALUNO' | 'RESPONSAVEL' | 'COLABORADOR';
      nome: string;
      foto?: string;
      selo?: 'ANIVERSARIO';
      linhas?: LinhaMia[];
      acoes?: AcaoMia[];
    }
  | {
      tipo: 'lista';
      titulo?: string;
      total?: number;
      itens: { titulo: string; subtitulo?: string; foto?: string; acao?: AcaoMia }[];
    }
  | { tipo: 'resumo'; titulo?: string; itens: LinhaMia[]; rodape?: string }
  | { tipo: 'tabela'; titulo?: string; colunas: string[]; linhas: string[][] }
  | { tipo: 'botoes'; acoes: AcaoMia[] }
  | { tipo: 'sugestoes'; itens: string[] }
  | { tipo: 'resultado'; titulo: string; texto?: string; acao?: AcaoMia }
  | { tipo: 'imagens'; itens: { url: string; legenda?: string }[] }
  | { tipo: 'acao_interface'; acao: AcaoInterfaceMia };

export type AssistenteMensagemResponse = {
  idConversa: number;
  idMensagem: number;
  texto: string;
  links: AssistenteLink[];
  proposta?: AssistenteProposta | null;
  blocos?: unknown[];
};

export type AssistenteConfirmacao = {
  id: number;
  situacao: string;
  idPublicacao: number;
  idAluno: number;
  data: string;
  /** Rota interna do que foi gravado. */
  caminho?: string;
  rotulo?: string;
  /** No lote: quem o servico recusou, com o motivo. */
  detalhes?: string[];
};

export type AssistenteMensagemItem = {
  id: number;
  papel: 'USUARIO' | 'ASSISTENTE';
  texto: string;
  links?: AssistenteLink[];
  blocos?: unknown[];
  nota?: -1 | 1 | null;
  criadoEm?: string;
  anexos?: AnexoMia[];
};

export type AssistenteHistorico = {
  idConversa: number;
  titulo?: string | null;
  mensagens: AssistenteMensagemItem[];
};

export type ConversaMia = {
  id: number;
  titulo: string | null;
  fixada: boolean;
  ultimaMensagemEm: string | null;
};

/** alvos: elementos marcados com data-alvo na tela, que a Mia pode destacar. */
export type ContextoTela = { rota: string; titulo?: string; alvos?: { id: string; rotulo?: string }[] };

export type AudioParaTranscricao = {
  blob: Blob;
  mimeType: string;
  duracaoSegundos: number;
  fileName: string;
};

export const assistenteService = {
  /** Conversa mais recente (rota antiga). */
  historico: async (): Promise<AssistenteHistorico> => {
    const { data } = await apiClient.get<AssistenteHistorico>('/v1/assistente/mensagens');
    return data;
  },
  historicoConversa: async (idConversa: number): Promise<AssistenteHistorico> => {
    const { data } = await apiClient.get<AssistenteHistorico>(`/v1/assistente/conversas/${idConversa}/mensagens`);
    return data;
  },
  conversas: async (busca?: string): Promise<ConversaMia[]> => {
    const termo = busca?.trim();
    const { data } = await apiClient.get<ConversaMia[]>('/v1/assistente/conversas', {
      params: termo ? { busca: termo.slice(0, 100) } : undefined,
    });
    return data;
  },
  novaConversa: async (): Promise<ConversaMia> => {
    const { data } = await apiClient.post<ConversaMia>('/v1/assistente/conversas');
    return data;
  },
  atualizarConversa: async (
    idConversa: number,
    mudanca: { titulo?: string; fixada?: boolean; arquivada?: boolean },
  ): Promise<ConversaMia> => {
    const { data } = await apiClient.patch<ConversaMia>(`/v1/assistente/conversas/${idConversa}`, mudanca);
    return data;
  },
  transcrever: async (audio: AudioParaTranscricao): Promise<{ texto: string }> => {
    const body = new FormData();
    body.append('arquivo', new File([audio.blob], audio.fileName, { type: audio.mimeType }));
    body.append('duracaoSegundos', String(audio.duracaoSegundos));
    const { data } = await apiClient.post<{ texto: string }>('/v1/assistente/transcricoes', body);
    return data;
  },
  /** Sem idConversa, a API usa a conversa mais recente. */
  enviar: async (
    texto: string,
    idConversa?: number | null,
    contexto?: ContextoTela | null,
    anexos: number[] = [],
  ): Promise<AssistenteMensagemResponse> => {
    const corpo = corpoDaFala(texto, contexto, anexos);
    const caminho = idConversa ? `/v1/assistente/conversas/${idConversa}/mensagens` : '/v1/assistente/mensagens';
    const { data } = await apiClient.post<AssistenteMensagemResponse>(caminho, corpo);
    return data;
  },
  /**
   * Mesma fala por SSE: onEtapa recebe "Procurando o aluno" etc. e onTexto os pedacos da fala
   * enquanto a Mia escreve. Se o fluxo for recusado antes de abrir (token vencido, escola,
   * navegador sem suporte), cai no POST comum, que renova a sessao. Erro depois de aberto
   * nao repete, para nao gravar a fala duas vezes.
   */
  enviarComEtapas: async (
    texto: string,
    idConversa: number | null | undefined,
    contexto: ContextoTela | null | undefined,
    onEtapa: (etapa: string) => void,
    onTexto: (delta: string) => void = () => undefined,
    anexos: number[] = [],
  ): Promise<AssistenteMensagemResponse> => {
    if (!idConversa || !fluxoDisponivel()) {
      return assistenteService.enviar(texto, idConversa, contexto, anexos);
    }
    const corpo = corpoDaFala(texto, contexto, anexos);
    return lerRespostaPorFluxo(`/v1/assistente/conversas/${idConversa}/mensagens/stream`, corpo, onEtapa, onTexto, () =>
      assistenteService.enviar(texto, idConversa, contexto, anexos),
    );
  },
  /** Sugestoes do momento para abrir a conversa (R23). */
  sugestoes: async (): Promise<SugestaoMomentoMia[]> => {
    const { data } = await apiClient.get<SugestaoMomentoMia[]>('/v1/assistente/sugestoes');
    return data;
  },
  /** Saudacao com agenda de hoje, notificacoes nao lidas e pendencias (R26). */
  novidades: async (): Promise<NovidadesMia> => {
    const { data } = await apiClient.get<NovidadesMia>('/v1/assistente/novidades');
    return data;
  },
  /** Sobe a foto ou o PDF como anexo de publicacao: a Mia le e pode por no recado. */
  enviarAnexo: async (arquivo: File): Promise<AnexoMia> => {
    const body = new FormData();
    body.append('arquivo', arquivo);
    body.append('tipoUso', 'anexo-publicacao');
    const { data } = await apiClient.post<{ id: number; nome: string; tipoConteudo: string; urlThumb?: string }>(
      '/v1/arquivos',
      body,
    );
    const pdf = data.tipoConteudo === 'application/pdf';
    return { idArquivo: data.id, nome: data.nome, tipo: pdf ? 'pdf' : 'imagem', urlThumb: pdf ? null : data.urlThumb };
  },
  /** Refaz a ultima resposta da Mia (a anterior sai do historico quando a nova fica pronta). */
  regenerar: async (idMensagem: number): Promise<AssistenteMensagemResponse> => {
    const { data } = await apiClient.post<AssistenteMensagemResponse>(`/v1/assistente/mensagens/${idMensagem}/regenerar`);
    return data;
  },
  regenerarComEtapas: async (
    idMensagem: number,
    onEtapa: (etapa: string) => void,
    onTexto: (delta: string) => void = () => undefined,
  ): Promise<AssistenteMensagemResponse> => {
    if (!fluxoDisponivel()) return assistenteService.regenerar(idMensagem);
    return lerRespostaPorFluxo(`/v1/assistente/mensagens/${idMensagem}/regenerar/stream`, {}, onEtapa, onTexto, () =>
      assistenteService.regenerar(idMensagem),
    );
  },
  avaliar: async (idMensagem: number, nota: -1 | 0 | 1, motivo?: string): Promise<void> => {
    const corpo = motivo?.trim() ? { nota, motivo: motivo.trim().slice(0, 500) } : { nota };
    await apiClient.post(`/v1/assistente/mensagens/${idMensagem}/feedback`, corpo);
  },
  /** campos: so o que a pessoa mudou no cartao. */
  confirmar: async (id: number, campos?: Record<string, string>): Promise<AssistenteConfirmacao> => {
    const corpo = campos && Object.keys(campos).length > 0 ? { campos } : undefined;
    const { data } = await apiClient.post<AssistenteConfirmacao>(`/v1/assistente/propostas/${id}/confirmar`, corpo);
    return data;
  },
  cancelar: async (id: number): Promise<void> => {
    await apiClient.post(`/v1/assistente/propostas/${id}/cancelar`);
  },
};

function corpoDaFala(texto: string, contexto: ContextoTela | null | undefined, anexos: number[]) {
  return {
    texto,
    ...(contexto ? { contexto } : {}),
    ...(anexos.length > 0 ? { anexos } : {}),
  };
}

function fluxoDisponivel(): boolean {
  return typeof fetch === 'function' && typeof TextDecoder !== 'undefined';
}

async function lerRespostaPorFluxo(
  caminho: string,
  corpo: unknown,
  onEtapa: (etapa: string) => void,
  onTexto: (delta: string) => void,
  semFluxo: () => Promise<AssistenteMensagemResponse>,
): Promise<AssistenteMensagemResponse> {
  const estado: { resposta: AssistenteMensagemResponse | null; erro: string | null; recebeu: boolean } = {
    resposta: null,
    erro: null,
    recebeu: false,
  };
  try {
    await postEventStream(caminho, corpo, ({ evento, dado }) => {
      estado.recebeu = true;
      const no = dado && typeof dado === 'object' ? (dado as Record<string, unknown>) : null;
      if (evento === 'etapa' && typeof no?.texto === 'string') onEtapa(no.texto);
      else if (evento === 'texto' && typeof no?.delta === 'string') onTexto(no.delta);
      else if (evento === 'resposta' && no) estado.resposta = no as unknown as AssistenteMensagemResponse;
      else if (evento === 'erro') estado.erro = typeof no?.mensagem === 'string' ? no.mensagem : 'Falha na resposta.';
    });
  } catch (falha) {
    if (falha instanceof FalhaAntesDoFluxo || (falha instanceof TypeError && !estado.recebeu)) {
      return semFluxo();
    }
    throw falha;
  }
  if (estado.resposta) return estado.resposta;
  throw new Error(estado.erro ?? 'A Mia nao respondeu.');
}
